import { useCallback, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import { useBulkGrantAccessMutation } from '../../api/user-invitation-api-client'
import { extractError } from '../../api/extract-error'
import type {
  BulkGrantAccessItemDto,
  BulkGrantResultItem,
} from '../../api/dto/user-invitation.dto'
import {
  failedEmails,
  mergeRetriedResults,
} from '../UserManagement/grantReport'

interface AccessGrantRun {
  /** What the submit achieved, so the report says what it is reporting on. */
  context: string
  /** Kept so a retry can reissue the failed addresses with the same roles. */
  grants: BulkGrantAccessItemDto[]
  /** Company names per address, for the report. Keys are not sent to the API. */
  companiesByEmail: Record<string, string[]>
  /** Null until the run lands. */
  results: BulkGrantResultItem[] | null
  isRunning: boolean
}

/**
 * The funding access a sync submit hands out, and its per-address outcome.
 * Ticking an address asks for access, not for an email: the server invites,
 * reissues an invitation, or assigns the role depending on the account behind
 * it. That plus the usual partly-done run is why the outcomes are held for the
 * operator to read and retry rather than counted into a toast.
 *
 * @param fallbackError - shown when the whole request fails, so each flow can
 * say what did survive.
 */
export const useAccessGrantRun = (fallbackError: string) => {
  const [run, setRun] = useState<AccessGrantRun | null>(null)
  const [progress, setProgress] = useState<{
    processed: number
    total: number
  } | null>(null)

  const mutation = useBulkGrantAccessMutation({
    onProgress: (processed, total) => setProgress({ processed, total }),
    onSuccess: ({ results: outcomes }) => {
      setProgress(null)
      setRun((curr) =>
        isNil(curr)
          ? null
          : {
              ...curr,
              isRunning: false,
              results: mergeRetriedResults(curr.results, outcomes),
            }
      )
    },
    onError: (error) => {
      setProgress(null)
      notification.error({ message: extractError(error, fallbackError) })
      // Nothing landed on a first attempt, so there is no report to hold open.
      // A failed retry keeps the one it already has.
      setRun((curr) =>
        isNil(curr) || isNil(curr.results)
          ? null
          : { ...curr, isRunning: false }
      )
    },
  })

  const grantAccess = useCallback(
    (
      context: string,
      grants: BulkGrantAccessItemDto[],
      companiesByEmail: Record<string, string[]> = {}
    ) => {
      if (grants.length === 0) return
      setRun({
        context,
        grants,
        companiesByEmail,
        results: null,
        isRunning: true,
      })
      setProgress(null)
      mutation.mutate({ grants })
    },
    [mutation]
  )

  const handleRetryFailed = useCallback(() => {
    if (isNil(run)) return
    const wanted = new Set(failedEmails(run.results))
    const retried = run.grants.filter((grant) => wanted.has(grant.email))
    if (retried.length === 0) return
    setRun({ ...run, isRunning: true })
    mutation.mutate({ grants: retried })
  }, [run, mutation])

  const handleClose = useCallback(() => {
    setRun(null)
    setProgress(null)
  }, [])

  return {
    isOpen: !isNil(run),
    context: run?.context ?? '',
    companiesByEmail: run?.companiesByEmail ?? {},
    results: run?.results ?? null,
    isRunning: run?.isRunning ?? false,
    progress,
    grantAccess,
    handleRetryFailed,
    handleClose,
  }
}
